'use client';

/**
 * NotificationInitializer Component
 *
 * Invisible component that initializes notification system:
 * - Registers service worker for Web Push
 * - Sets up Supabase Realtime subscription
 * - Plays notification sounds
 *
 * Place this in a layout or provider to initialize once.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import logger from '@/lib/logger';
import { useServiceWorker } from '@/hooks/useServiceWorker';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { useNotifications } from '@/hooks/useNotifications';
import { addNotification, selectPushSubscribed } from '@/store/slices/notificationsSlice';
import type { Notification } from '@/store/slices/notificationsTypes';
import { registerToastSink, type RetainedToastRecord } from '@/lib/notifications/toastSink';
import { retainedToastToNotification } from '@/lib/notifications/retainedToastRecord';

interface NotificationInitializerProps {
  /** User ID for realtime subscription */
  userId: string | null;
  /** Whether to enable notifications */
  enabled?: boolean;
}

export function NotificationInitializer({
  userId,
  enabled = true,
}: NotificationInitializerProps) {
  const isPushSubscribed = useAppSelector(selectPushSubscribed);
  const prevUnreadRef = useRef<number>(0);
  const dispatch = useAppDispatch();

  // Initialize service worker
  const { isRegistered, error: swError } = useServiceWorker({
    autoRegister: enabled,
  });

  // Sound playback
  const { playSound, isInQuietHours } = useNotificationSound({
    enabled: enabled,
  });

  // Handle new notification arrival - MUST be memoized to prevent infinite re-subscription loop
  const handleNewNotification = useCallback((notification: Notification) => {
    // Play sound if not in quiet hours and enabled
    if (!isInQuietHours && !notification.isRead) {
      // Use different sounds based on priority
      if (notification.priority === 'urgent' || notification.priority === 'high') {
        playSound('alert');
      } else {
        playSound('default');
      }
    }

    // Show in-app toast/snackbar could be added here
    logger.debug('NotificationInitializer', 'New notification received', { title: notification.title });
  }, [isInQuietHours, playSound]);

  // Subscribe to realtime notifications
  const { isSubscribed: isRealtimeSubscribed } = useNotificationRealtime({
    userId,
    enabled: enabled && !!userId,
    onNotification: handleNewNotification,
  });

  // Fallback: poll notifications when realtime is unavailable
  const { refresh } = useNotifications({ autoFetch: false });

  useEffect(() => {
    if (!enabled || !userId || isRealtimeSubscribed) {
      return;
    }

    const interval = window.setInterval(() => {
      refresh();
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, [enabled, userId, isRealtimeSubscribed, refresh]);

  // CARRY RETAINED TOASTS TO THE BELL.
  //
  // This is the missing half of the owner's report: a toast vanished and left
  // no record, because the toast layer had no route into the notification
  // store at all. showUnifiedToast now hands every retained toast to a sink;
  // this is that sink. Two writes, deliberately:
  //   - Redux immediately, so the bell shows it without waiting on a network
  //     round trip (and so it still works when the POST is refused);
  //   - the durable store, so it survives a reload. In-memory alone was the
  //     existing /calls/* behaviour and it is lost on refresh, which is the
  //     detail that made "you have no idea what they said" unarguable.
  // A refused or failed durable write logs at ERROR: the bell entry is still
  // there for this session, but its durability is gone, and that is a failsafe
  // firing rather than a normal outcome.
  useEffect(() => {
    if (!enabled) return;

    return registerToastSink((record: RetainedToastRecord) => {
      const notification = retainedToastToNotification(record, userId);
      dispatch(addNotification(notification));

      void (async () => {
        try {
          const response = await fetch('/api/notifications', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              category: notification.category,
              priority: notification.priority,
              behavior: notification.behavior,
              title: notification.title,
              body: notification.body,
              source_page: record.sourcePath,
              metadata: notification.metadata,
            }),
          });
          if (!response.ok) {
            logger.error(
              'NotificationInitializer',
              'A retained notification did not reach the durable store — it will not survive a reload',
              { status: response.status, title: notification.title },
            );
          }
        } catch (error) {
          logger.error(
            'NotificationInitializer',
            'A retained notification did not reach the durable store — it will not survive a reload',
            { title: notification.title, error: error instanceof Error ? error.message : String(error) },
          );
        }
      })();
    });
  }, [enabled, userId, dispatch]);

  // Log initialization status
  useEffect(() => {
    if (enabled) {
      logger.debug('NotificationInitializer', 'Initializing', {
        userId,
        serviceWorker: isRegistered ? 'registered' : 'pending',
        realtime: isRealtimeSubscribed ? 'subscribed' : 'pending',
        pushSubscribed: isPushSubscribed,
      });
    }
  }, [enabled, userId, isRegistered, isRealtimeSubscribed, isPushSubscribed]);

  // Log errors
  useEffect(() => {
    if (swError) {
      logger.error('NotificationInitializer', 'Service worker error', { error: swError });
    }
  }, [swError]);

  // This component doesn't render anything visible
  return null;
}

export default NotificationInitializer;
